import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ApiCallService } from '@pkg-services/callApi/api-call.service';
import { RouteAdminProspectionCallbacks } from '@pkg-framework/models/admin';

/** Ce qu'il faut pour composer le numéro : une ligne du Plan de rappel, réduite. */
interface UpcomingCallback {
  uuid: string;
  target: 'organization' | 'mairie';
  name: string;
  first_name: string;
  phone: string;
  source_label: string;
  step: string | null;
  message: string | null;
  client: boolean;
  bucket: 'now' | 'overdue' | 'unscheduled' | 'today' | 'tomorrow' | 'later';
  next_slot: { day_label: string; hours: string } | null;
  slots: { day_label: string }[];
}

/**
 * Les prochains appels à passer (02/11/2026), clients d'abord : dans le menu de l'icône téléphone de l'en-tête
 * et en haut du tableau de bord. Le détail et le résultat d'un appel restent dans Prospection › Rappels.
 */
@Component({
  selector: 'app-upcoming-callbacks',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './upcoming-callbacks.component.html',
  styleUrl: './upcoming-callbacks.component.scss',
})
export class UpcomingCallbacksComponent implements OnChanges {
  /** Nombre de lignes affichées. */
  @Input() limit = 5;
  /** Change pour relire (ouverture du menu de l'en-tête). */
  @Input() reloadKey = 0;
  @Output() navigate = new EventEmitter<void>();

  rows: UpcomingCallback[] = [];
  total = 0;
  clients = 0;
  loading = false;
  /** Pas le droit prospection:manage, ou table absente : on ne montre rien. */
  unavailable = false;

  constructor(private api: ApiCallService) {}

  ngOnChanges(): void {
    this.load();
  }

  private load(): void {
    this.loading = true;
    this.api.get(RouteAdminProspectionCallbacks).subscribe({
      next: (res: any) => {
        const plan = res?.data;
        this.loading = false;
        this.unavailable = !plan?.available;
        const pending: UpcomingCallback[] = plan?.pending ?? [];
        // Clients d'abord, puis l'ordre du plan (maintenant, en retard, à planifier, aujourd'hui…).
        const sorted = [...pending.filter((r) => r.client), ...pending.filter((r) => !r.client)];
        this.total = pending.length;
        this.clients = pending.filter((r) => r.client).length;
        this.rows = sorted.slice(0, this.limit);
      },
      error: () => {
        this.loading = false;
        this.unavailable = true;
      },
    });
  }

  when(row: UpcomingCallback): string {
    if (row.next_slot) return `${row.next_slot.day_label}, ${row.next_slot.hours}`;
    if (row.slots.length) return 'En retard';
    return 'À planifier';
  }

  late(row: UpcomingCallback): boolean {
    return row.bucket === 'overdue' || row.bucket === 'unscheduled' || row.bucket === 'now';
  }

  /** Client : son motif ; prospect : où il s'est arrêté. */
  detail(row: UpcomingCallback): string {
    if (row.client) {
      const topic = row.source_label.replace(/^Client · /, '');
      return topic.charAt(0).toUpperCase() + topic.slice(1);
    }
    return row.step ? `Arrêté à : ${row.step}` : row.target === 'mairie' ? 'Mairie' : 'Prospect';
  }

  telHref(phone: string): string {
    return 'tel:' + phone.replace(/[^\d+]/g, '');
  }
}
